import { Task, Timestamp, UpcomingPeriod, WeekDays } from '@griply/types';
import { getScheduleByDate, weekdaysNumberMap } from '@griply/utils';
import { addDays, format, isSameDay, startOfWeek } from 'date-fns';
import { useUpdateTask } from 'features/tasks';
import React, { useMemo, useState } from 'react';
import { Check } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';
import { updateWeekOccurrences } from 'shared/utils/schedules';

import * as Styled from './week-picker.style';

export type WeekPickerProps = {
  task: Task;
  referenceElement: React.RefObject<HTMLElement>;
  onOutside: () => void;
  period: UpcomingPeriod;
  weekStartsOn: WeekDays;
  disableAutoUpdate?: boolean;
};

export const WeekPicker: React.FC<WeekPickerProps> = ({
  task,
  referenceElement,
  onOutside,
  period,
  weekStartsOn,
  disableAutoUpdate,
}) => {
  const today = useToday();
  const { submit: updateTask } = useUpdateTask();

  const [selectedDates, setSelectedDates] = useState<Date[]>(() =>
    period.days
      .filter((d) => {
        if (!d.entry || d.entry.hidden) {
          return false;
        }

        // Check if the schedule for this day is frequency-based
        const schedule = getScheduleByDate(task.schedules, {
          date: d.date,
          today,
        });
        const isFrequencyHabit = !!schedule?.frequency;

        // For frequency habits, we strictly only want explicitly planned occurrences (not generated placeholders)
        return !(isFrequencyHabit && d.entry.isGeneratedFrequencyEntry);
      })
      .map((d) => d.date),
  );

  const handleUpdate = (dates: Timestamp[]) => {
    if (!task.schedules?.length) {
      return;
    }

    const currentDates = period.days
      .filter((dayEntry) => {
        if (!dayEntry.entry || dayEntry.entry.hidden) {
          return false;
        }

        // Check if the schedule for this day is frequency-based
        const schedule = getScheduleByDate(task.schedules, {
          date: dayEntry.date,
          today,
        });
        const isFrequencyHabit = !!schedule?.frequency;

        // For frequency habits, we strictly only want explicitly planned occurrences (not generated placeholders)
        // This ensures that when we save, we treat generated ones as "not valid" so they get correctly converted to planned/explicit ones
        return !(isFrequencyHabit && dayEntry.entry.isGeneratedFrequencyEntry);
      })
      .map((d) => d.date);

    const schedules = updateWeekOccurrences(
      task.schedules,
      currentDates,
      dates,
      period.date,
      { today, weekStartsOn },
    );

    updateTask({ id: task.id, schedules });
  };

  const weekDays = useMemo(() => {
    const start = startOfWeek(period.date, {
      weekStartsOn: weekdaysNumberMap[weekStartsOn],
    });
    return Array.from({ length: 7 }).map((_, index) => addDays(start, index));
  }, [period.date, weekStartsOn]);

  const toggleDate = (date: Date) => {
    const isSelected = selectedDates.some((d) => isSameDay(d, date));

    let updatedDates: Date[] = [];

    if (isSelected) {
      updatedDates = selectedDates.filter((d) => !isSameDay(d, date));
    }

    if (!isSelected) {
      updatedDates = [...selectedDates, date];
    }

    setSelectedDates(updatedDates);
    handleUpdate(updatedDates);
  };

  return (
    <PopupMenu
      referenceElement={referenceElement}
      onOutside={onOutside}
      disableAutoUpdate={disableAutoUpdate}
    >
      <PopupMenuList>
        {weekDays.map((date) => {
          const isSelected = selectedDates.some((d) => isSameDay(d, date));
          const _onClick = () => toggleDate(date);

          return (
            <PopupMenuListItem key={date.toISOString()}>
              <PopupMenuButton
                onClick={_onClick}
                start={
                  <Styled.IconContainer>
                    {isSelected && <Icon icon={Check} />}
                  </Styled.IconContainer>
                }
              >
                {format(date, 'EEEE')}
              </PopupMenuButton>
            </PopupMenuListItem>
          );
        })}
      </PopupMenuList>
    </PopupMenu>
  );
};
